import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { BookOpen, Play, LogIn, Library, History } from "lucide-react";

const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const LOGIN_URL = `${process.env.REACT_APP_BACKEND_URL}/api/audio/auth/google/login?next=${encodeURIComponent("/ecomics/library")}`;

export default function EComicsLibrary() {
  const [me, setMe] = useState(undefined);
  const [volumes, setVolumes] = useState([]);
  const [shelf, setShelf] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/ecomics/auth/me").then(r => r.data).catch(() => ({ user: null, purchased_volume_ids: [] })),
      api.get("/ecomics/volumes").then(r => r.data).catch(() => []),
    ]).then(([m, v]) => {
      setMe(m); setVolumes(v);
      if (m.user) api.get("/ecomics/my/shelf").then(r => setShelf(r.data)).catch(() => {});
    });
  }, []);

  if (me === undefined) {
    return <PublicLayout><div className="p-24 text-center text-[var(--bb-muted)]">Loading…</div></PublicLayout>;
  }

  const owned = volumes.filter(v => me.purchased_volume_ids.includes(v.volume_id));

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20" data-testid="ecomics-library-page">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="bb-overline text-[var(--bb-terracotta)] mb-3 flex items-center justify-center gap-2">
            <Library size={12}/> MY E-COMICS
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[var(--bb-forest)] tracking-tight">My Library</h1>
          {me.user && <p className="mt-3 text-sm text-[var(--bb-muted)]" data-testid="ecomics-library-user">Signed in as {me.user.name || me.user.email}</p>}
        </div>

        {!me.user ? (
          <div className="bb-card p-12 text-center bg-white max-w-md mx-auto" data-testid="ecomics-library-signin">
            <LogIn size={28} className="mx-auto text-[var(--bb-terracotta)] mb-4"/>
            <div className="font-serif text-2xl text-[var(--bb-forest)]">Sign in to see your shelf</div>
            <p className="mt-2 text-sm text-[var(--bb-muted)]">Your purchased volumes appear here on any device.</p>
            <a href={LOGIN_URL} className="mt-6 bb-btn-primary inline-flex items-center gap-2" data-testid="ecomics-library-signin-btn">
              <LogIn size={15}/> Sign in with Google
            </a>
          </div>
        ) : (
          <>
          {shelf?.continue_reading?.length > 0 && (
            <div className="mb-12" data-testid="ecomics-continue-shelf">
              <div className="bb-overline text-[var(--bb-muted)] mb-4 flex items-center gap-2"><History size={13}/> CONTINUE READING</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {shelf.continue_reading.map(c => (
                  <Link
                    key={c.vachana_id}
                    to={`/ecomics/read/${c.volume_id}/${c.vachana_id}`}
                    className="bb-card p-4 bg-white flex items-center gap-4 hover:shadow-[0_14px_30px_-18px_rgba(44,53,49,0.3)] transition"
                    data-testid={`ecomics-continue-${c.vachana_id}`}
                  >
                    <div className="w-12 h-16 rounded overflow-hidden bg-[var(--bb-sand)] ring-1 ring-[var(--bb-border)] shrink-0 grid place-items-center">
                      {c.cover_path ? <img src={FILES + c.cover_path} alt="" className="w-full h-full object-cover"/> : <BookOpen size={16} className="text-[var(--bb-muted)]"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[var(--bb-forest)] truncate">{c.kannada_title}</div>
                      <div className="text-xs text-[var(--bb-muted)] truncate">Vol {c.volume_number} · {c.english_title}</div>
                      {c.total_pages > 0 && (
                        <div className="mt-1.5 h-1.5 rounded-full bg-[var(--bb-sand)] overflow-hidden">
                          <div className="h-full bg-[var(--bb-terracotta)] rounded-full" style={{ width: `${Math.min(100, Math.round(((c.last_page_index + 1) / c.total_pages) * 100))}%` }}/>
                        </div>
                      )}
                      <div className="mt-1 text-[10px] text-[var(--bb-muted)]">Page {c.last_page_index + 1} of {c.total_pages}</div>
                    </div>
                    <Play size={16} className="text-[var(--bb-terracotta)] shrink-0"/>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {owned.length === 0 ? (
          <div className="bb-card p-12 text-center bg-white max-w-md mx-auto" data-testid="ecomics-library-empty">
            <BookOpen size={28} className="mx-auto text-[var(--bb-terracotta)] mb-4"/>
            <div className="font-serif text-2xl text-[var(--bb-forest)]">Your shelf is empty</div>
            <p className="mt-2 text-sm text-[var(--bb-muted)]">Volumes you buy will appear here.</p>
            <Link to="/ecomics" className="mt-6 bb-btn-primary inline-block" data-testid="ecomics-library-browse-btn">Browse volumes</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {owned.map(v => (
              <div key={v.volume_id} className="bb-card p-5 bg-white flex flex-col" data-testid={`ecomics-library-volume-${v.volume_id}`}>
                <div className="aspect-[3/4] rounded overflow-hidden bg-[var(--bb-sand)] mb-5 ring-1 ring-[var(--bb-border)]">
                  {v.cover_path ? (
                    <img src={FILES + v.cover_path} alt={v.title} loading="lazy" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full grid place-items-center text-[var(--bb-muted)]"><BookOpen size={36}/></div>
                  )}
                </div>
                <div className="bb-overline text-[var(--bb-muted)] mb-1">VOLUME {v.volume_number} · OWNED</div>
                <h3 className="font-serif text-xl text-[var(--bb-forest)] leading-snug mb-3 flex-1">{v.title}</h3>
                {(() => {
                  const vp = shelf?.volume_progress?.[v.volume_id];
                  if (!vp || vp.total_published === 0) return null;
                  return (
                    <div className="mb-4" data-testid={`ecomics-volume-progress-${v.volume_id}`}>
                      <div className="h-2 rounded-full bg-[var(--bb-sand)] overflow-hidden">
                        <div className="h-full bg-[var(--bb-terracotta)] rounded-full transition-all" style={{ width: `${Math.round((vp.finished_count / vp.total_published) * 100)}%` }}/>
                      </div>
                      <div className="mt-1.5 text-xs text-[var(--bb-muted)]">{vp.finished_count} of {vp.total_published} vachanas read</div>
                    </div>
                  );
                })()}
                <div className="flex gap-2">
                  <Link
                    to={v.first_vachana_id ? `/ecomics/read/${v.volume_id}/${v.first_vachana_id}` : `/ecomics/volume/${v.volume_id}`}
                    className="bb-btn-primary !py-2 text-sm flex-1 text-center inline-flex items-center justify-center gap-1.5"
                    data-testid={`ecomics-library-read-${v.volume_id}`}
                  >
                    <Play size={13}/> Read
                  </Link>
                  <Link to={`/ecomics/volume/${v.volume_id}`} className="bb-btn-secondary !py-2 text-sm px-4" data-testid={`ecomics-library-detail-${v.volume_id}`}>
                    Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </section>
    </PublicLayout>
  );
}
